import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { generateClient } from 'aws-amplify/api';
import { getDocument, listDocuments } from '../amp-graphql/queries';
import { GetDocumentQuery, ListDocumentsQuery } from '../amp-graphql/API';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import { onDocumentUpdated } from '../amp-graphql/subscriptions';
import { environment } from '../../environments/environment';
import { getAuthToken } from '../graphql/amplify.config';
import { isGraphqlAuthFailure } from '../utils/auth-errors';

export type DocumentStatus = 'persisted' | 'processing' | 'done' | 'error';

@Injectable({
  providedIn: 'root'
})
export class OcrHistoryService {
  private ampClient = () => generateClient({});

  constructor(
    private authService: AuthService
  ) {
  }

  getHistory(status: DocumentStatus, limit = 50, nextToken?: string | null): Observable<ListDocumentsQuery> {
    return this.withSessionExpiry(fromPromise(this.ampClient().graphql({
      query: listDocuments,
      variables: { status, limit, nextToken: nextToken ?? null }
    }).then(response => response.data)));
  }

  getDocumentByS3Key(s3Key: string): Observable<GetDocumentQuery> {
    return this.withSessionExpiry(fromPromise(this.ampClient().graphql({
      query: getDocument,
      variables: { s3Key }
    }).then(response => {
      return response.data;
    })));
  }

  private withSessionExpiry<T>(source: Observable<T>): Observable<T> {
    return source.pipe(
      catchError(error => {
        if (isGraphqlAuthFailure(error)) {
          this.authService.handleSessionExpired();
        }
        return throwError(() => error);
      })
    );
  }

  onDocumentUpdated(): Observable<any> {
    return new Observable<any>((observer) => {
      let socket: WebSocket | null = null;
      let reconnectAttempt = 0;
      const maxReconnectAttempts = 5;
      let reconnectTimeout: any = null;

      // Function to create and setup the WebSocket connection
      const connect = () => {
        const socketUrl = environment.appsync.realtimeEndpoint;
        const host = environment.appsync.httpEndpoint.replace('https://', '').replace('/graphql', '');
        const headers = {
          host,
          Authorization: `Bearer ${getAuthToken()}`
        };
        const encodedHeader = btoa(JSON.stringify(headers));
        const payLoad = btoa(JSON.stringify({}));

        // Close existing socket if it exists
        if (socket) {
          socket.close();
        }

        socket = new WebSocket(`${socketUrl}?header=${encodedHeader}&payload=${payLoad}`, ['graphql-ws']);

        socket.onopen = () => {
          console.log('WebSocket connected');
          reconnectAttempt = 0; // Reset reconnect attempts on successful connection

          // Initialize the connection
          socket?.send(
            JSON.stringify({
              type: 'connection_init'
            })
          );
        };

        socket.onclose = (event) => {
          // Attempt to reconnect if not closed intentionally
          if (!event.wasClean) {
            reconnectAttempt++;
            if (reconnectAttempt <= maxReconnectAttempts) {
              const delay = Math.min(1000 * Math.pow(2, reconnectAttempt - 1), 30000); // Exponential backoff with max of 30 seconds
              console.log(`Attempting to reconnect (${reconnectAttempt}/${maxReconnectAttempts}) in ${delay}ms...`);

              // Clear any existing timeout
              if (reconnectTimeout) {
                clearTimeout(reconnectTimeout);
              }

              // Set timeout for reconnection
              reconnectTimeout = setTimeout(() => {
                connect();
              }, delay);
            } else {
              console.error('Maximum reconnection attempts reached');
              observer.error(new Error('Maximum reconnection attempts reached'));
            }
          }
        };

        socket.onmessage = (event) => {
          const data = JSON.parse(event.data);
          if (data.type === 'connection_ack') {
            socket?.send(JSON.stringify({
              id: crypto.randomUUID(),
              type: 'start',
              payload: {
                data: JSON.stringify({
                  query: onDocumentUpdated
                }),
                extensions: {
                  authorization: headers
                }
              }
            }));
          }
          else if ((data.type === 'connection_error' || data.type === 'error') && isGraphqlAuthFailure(data.payload)) {
            this.authService.handleSessionExpired();
          }
          // Handle the actual subscription data
          else if (data.type === 'data' && data.payload && data.payload.data) {
            // Emit the received data to subscribers
            observer.next(data.payload.data.onDocumentUpdated);
          }
        };

        // Handle errors
        socket.onerror = (error) => {
          console.error('WebSocket error:', error);
          // Error will be handled by onclose event for reconnection
        };
      };

      // Initial connection
      connect();

      // Return cleanup function
      return () => {
        console.log('Closing WebSocket connection');
        if (reconnectTimeout) {
          clearTimeout(reconnectTimeout);
        }
        if (socket) {
          socket.close();
        }
      };
    });
  }
}
